import { useNavigate } from "@/lib/router-compat";
import { User, Settings, LogOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationToggle } from "@/components/NotificationToggle";
import { useTranslation } from "@/i18n/LanguageContext";
import { useSignedUrl } from "@/hooks/use-signed-url";
import { supabase } from "@/integrations/supabase/client";
import { StatusDot } from "@/components/StatusDot";
import husselAppLogo from "@/assets/hussel-app-logo.png";

interface AppHeaderProps {
  clubLogo?: string | null;
  clubName?: string;
  profilePhotoUrl?: string | null;
  profileFirstName?: string | null;
  navigateTo?: string;
  showNotificationToggle?: boolean;
  children?: React.ReactNode;
}

export const AppHeader = ({
  clubLogo,
  clubName,
  profilePhotoUrl,
  profileFirstName,
  navigateTo = "/dashboard",
  showNotificationToggle = false,
}: AppHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profilePhotoSignedUrl = useSignedUrl(profilePhotoUrl);
  const logo = clubLogo || husselAppLogo;
  const name = clubName || "De Hussel App";

  return (
    <div className="mb-4" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative flex-shrink-0">
            <img
              src={logo}
              alt={name}
              className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover cursor-pointer border-2 border-border"
              onClick={() => navigate(navigateTo)}
            />
            <StatusDot />
          </div>
          <h1 className="text-lg sm:text-xl font-semibold text-foreground">{name}</h1>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {showNotificationToggle && <NotificationToggle />}
          <DropdownMenu>
            <DropdownMenuTrigger className="relative focus:outline-none">
              <div className="relative">
                <Avatar className="h-10 w-10 sm:h-12 sm:w-12 border-2 border-background">
                  <AvatarImage src={profilePhotoSignedUrl || undefined} alt={profileFirstName || undefined} />
                  <AvatarFallback>
                    <User className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1">
                  <Settings className="h-3 w-3 text-primary-foreground" />
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-card z-[60]">
              <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                {t('profile.editProfile')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/account', { state: { tab: 'settings' } })} className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                {t('profile.appSettings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => {
                await supabase.auth.signOut();
                navigate('/');
              }} className="cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                {t('profile.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
